import React, { useState, useContext, useEffect } from "react";
import { useFormState, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { cloneDeep } from "lodash";
import moment from "moment";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";

import PropTypes from "prop-types";

import Label from "./Label";
import FormRecordList from "../FormRecordList";
import FormLicenseInfo from "../FormLicenseInfo";
import { ReviewContext } from "../../context/reviewItemContext";
import {
  useDropdownListMutation,
  useLazyGetDropdownListQuery,
} from "../../../../api/forms.api.slice";
import {
  generateDropdownUrl,
  getFieldValue,
  getFormattedValue,
  getParsedValue,
  getInvoiceUrl,
  getUpdatedOptionList,
} from "../../services/form.service";
import {
  getDgOptionList,
  isAssociateField,
} from "../../services/form.relationalDg.service";
import {
  formatPhoneNumber,
  getSocialSecurityNumber,
} from "../../../../utils/helpers";
import useGetFormStateValues from "../../hooks/useGetFormProps";
import useFormDispatch from "../../hooks/useFormDispatch";

export const Info = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "condition",
})(({ theme }) => ({
  marginTop: "-0.5rem",
  marginBottom: "1rem",
  borderWidth: "1px",
  borderStyle: "solid",
  borderRadius: "4px",
  color: "#254250",
  backgroundColor: "#b2c3ca",
  borderColor: "#bce8f1",
  padding: "0.625rem 1.25rem",
  ...theme.applyStyles("light", {
    color: "#31708f",
    backgroundColor: "#d9edf7",
  }),
  variants: [
    {
      props: ({ condition }) => condition.isDg,
      style: { marginTop: 0 },
    },
    {
      props: ({ condition }) => condition.isError,
      style: {
        color: "#ea7371",
        backgroundColor: "#f2dede",
        borderColor: "#ebccd1",
      },
    },
  ],
}));

const filterMap = {
  currency: "$",
  percentage: "%",
};
function FormReadOnlyField(props) {
  const {
    getValues,
    setValue,
    newFormProps,
    control,
    register,
    fieldProps: originalFieldProps,
    hasConditionalRequired,
    trigger: triggerValidation,
  } = props;
  const { isDialogForm, dispatchMisc } = useFormDispatch();
  const { formConfig = {} } = useGetFormStateValues();
  const { isSinglePageLayout, singlePageLayoutIcon, domainName } = formConfig;
  const { reviewProps } = useContext(ReviewContext);
  const applicationStatus = useSelector(
    (state) => state.formProps.applicationData?.status ?? "Open"
  );

  const fieldValue = useWatch({
    name: originalFieldProps.key,
    defaultValue: newFormProps[originalFieldProps.key],
    control,
    exact: true,
  });

  const labelFieldValue = useWatch({
    name: `${originalFieldProps.key}Label`,
    defaultValue: newFormProps[`${originalFieldProps.key}Label`],
    control,
    disabled: !originalFieldProps?.properties?.skipOptionMapping,
    exact: true,
  });

  const emailValidationMsg = useWatch({
    name: `${originalFieldProps.key}validationMsg__DUMMY`,
    defaultValue: newFormProps[`${originalFieldProps.key}validationMsg__DUMMY`],
    control,
    disabled: !originalFieldProps.properties?.carbonCopy,
    exact: true,
  });

  let defaultValues = {
    value: reviewProps ? fieldValue : newFormProps[originalFieldProps.key],
  };

  const [lazyTrigger, lazyResult] = useLazyGetDropdownListQuery();
  const [mutationTrigger, mutationResult] = useDropdownListMutation();
  const defaultFieldProps = {
    ...originalFieldProps,
    ...(defaultValues.value &&
      (originalFieldProps.type === "select" ||
        originalFieldProps.type === "selectboxes") &&
      originalFieldProps.dataSrc &&
      originalFieldProps.dataSrc === "url" &&
      originalFieldProps.data &&
      originalFieldProps.data.url && {
        urlValues: [{ label: "Loading ...", value: defaultValues.value }],
      }),
  };
  const [fieldProps, setFieldProps] = useState(defaultFieldProps);
  const { errors } = useFormState({
    control,
    name: fieldProps.key,
    exact: true,
  });
  const { dataSrc, data, properties = {}, description, hidden } = fieldProps;
  const { skipOptionMapping, customFilter, type, displayAs, controllingField } =
    properties || {};
  // Temp Fix
  const skipCache = data?.url && data.url.includes("licenses");
  const result = skipCache ? mutationResult : lazyResult;
  const isValueMasked = useWatch({
    name: `${fieldProps.key}isMasked__DUMMY`,
    defaultValue: true,
    control,
    exact: true,
    disabled: fieldProps.type !== "encrypted",
  });

  const condition = originalFieldProps?.validate?.custom
    ? getParsedValue(originalFieldProps.validate.custom, originalFieldProps.key)
    : undefined;

  const sourceDgKey = condition?.sourceDatagrid;
  // setting default value only for dialog form
  const defaultSourceDgValue =
    isDialogForm && sourceDgKey
      ? newFormProps[sourceDgKey] || newFormProps.applicationData?.[sourceDgKey]
      : undefined;

  const sourceDg = useWatch({
    name: sourceDgKey || null,
    disabled: sourceDgKey === undefined,
    defaultValue: defaultSourceDgValue,
  });

  useEffect(() => {
    let timeout = null;
    if (fieldProps.type === "select") {
      if (dataSrc === "url" && data && data.url && !skipOptionMapping) {
        const { url, body } = generateDropdownUrl(properties, data?.url, {
          ...getValues(),
        });
        if (body || skipCache) {
          mutationTrigger({ url, body });
        } else {
          lazyTrigger(url, true);
        }
      }
    }
    if (fieldProps.type === "selectboxes") {
      if (properties?.dataSrc) {
        const { url, body } = generateDropdownUrl(
          properties,
          properties?.dataSrc,
          { ...getValues() }
        );
        if (body || skipCache) {
          mutationTrigger({ url, body });
        } else {
          lazyTrigger(url, true);
        }
      }
      // store source dg identifiers in misc for dynamic field labeling
      if (!skipOptionMapping) {
        if (isAssociateField(condition) && Array.isArray(sourceDg)) {
          const menuItems = getDgOptionList(sourceDg, condition);
          dispatchMisc({ key: fieldProps.key, value: menuItems });
        }
      }
    }
    if (fieldProps.type === "recordList") {
      const userPrefTableView =
        JSON.parse(localStorage.getItem(`${fieldProps.key}TableView`)) ?? false;
      register(`${fieldProps.key}TableView__DUMMY`);
      setValue(`${fieldProps.key}TableView__DUMMY`, userPrefTableView);
    }
    if (!!reviewProps && defaultValues.value) {
      if (fieldProps.key.includes("-dt") || fieldProps.key.includes("-dtm"))
        timeout = setTimeout(() => triggerValidation(fieldProps.key), 10);
      else triggerValidation(fieldProps.key);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (result && result.data) {
      setFieldProps((prev) => ({ ...prev, urlValues: result.data }));
      if (hidden && controllingField) {
        const optionList = getUpdatedOptionList(result.data, fieldProps);
        if (optionList.length && optionList[0]?.value != null) {
          const nextValue = optionList[0].value;
          if (getValues(originalFieldProps.key) !== nextValue) {
            setValue(originalFieldProps.key, nextValue, {
              shouldValidate: true,
            });
          }
        }
      }
    }
    if (result && result.isError) {
      setFieldProps(originalFieldProps);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, originalFieldProps]);

  // Sync validate.required when parent resolves conditionalRequired
  useEffect(() => {
    if (!hasConditionalRequired) return;
    const incomingRequired = originalFieldProps.validate?.required;
    setFieldProps((prev) => {
      if (prev.validate?.required === incomingRequired) return prev;
      return {
        ...prev,
        validate: { ...prev.validate, required: incomingRequired },
      };
    });
  }, [hasConditionalRequired, originalFieldProps.validate?.required]);

  const formData = reviewProps ? getValues() : newFormProps;

  if (customFilter && fieldProps.type === "textfield") {
    defaultValues.value = getFormattedValue(
      filterMap[customFilter],
      defaultValues.value
    );
  }

  if (fieldProps.type === "select" || fieldProps.type === "selectboxes") {
    if (!skipOptionMapping) {
      const newFieldProps = cloneDeep(fieldProps);
      if (isAssociateField(condition) && Array.isArray(sourceDg)) {
        const menuItems = getDgOptionList(sourceDg, condition);
        if (fieldProps.type === "select") newFieldProps.data.values = menuItems;
        else newFieldProps.values = menuItems;
      }
      const value = getFieldValue(newFieldProps, formData, applicationStatus);
      defaultValues = { value };
    } else
      defaultValues.value = reviewProps
        ? labelFieldValue
        : newFormProps[`${originalFieldProps.key}Label`];
  }
  if (fieldProps.type === "radio") {
    const fieldData = getFieldValue(fieldProps, formData);
    defaultValues = fieldData;
  }
  if (fieldProps.type === "checkbox") {
    if (!defaultValues.value) defaultValues.value = "No";
    else defaultValues.value = "Yes";
    if (fieldProps.validate?.required && defaultValues.value === "No") {
      defaultValues.isError = true;
    }
  }

  let labelValue = defaultValues.value;
  if (fieldProps.type === "encrypted") {
    labelValue = getSocialSecurityNumber({
      isMasked: isValueMasked,
      value: defaultValues.value,
      fieldProps,
      isFormatted: true,
    });
  }
  if (fieldProps.type === "phoneNumber") {
    labelValue = formatPhoneNumber(labelValue);
  }
  if (fieldProps.type === "number") {
    const { prefix, suffix, decimalLimit } = fieldProps;
    if (prefix || suffix) {
      labelValue = getFormattedValue(
        prefix || suffix,
        defaultValues.value,
        decimalLimit
      );
    }
    if (properties?.valueAsNumber && labelValue === 0)
      labelValue = `${labelValue}`;
  }
  if (fieldProps.type === "readOnlyMapping") {
    const { validate } = fieldProps;
    if (validate?.custom) {
      const { prefix, suffix } = validate?.custom || {};
      if (prefix || suffix)
        labelValue = getFormattedValue(prefix || suffix, defaultValues.value);
    }
  }
  if (fieldProps.type === "autocomplete" && labelValue) {
    if (typeof labelValue === "object") labelValue = labelValue.display || "";
  }
  if (
    fieldProps.type === "datetime" &&
    fieldProps.key.endsWith("-dtm") &&
    labelValue
  ) {
    labelValue = moment(labelValue).format("MM/DD/YYYY hh:mm A");
  }

  const handleMaskedToggle = () => {
    setValue(`${fieldProps.key}isMasked__DUMMY`, !isValueMasked);
  };

  const requiredFieldCheck =
    (!!reviewProps || singlePageLayoutIcon) &&
    fieldProps.validate &&
    fieldProps.validate.required &&
    (Array.isArray(labelValue) ? labelValue.length === 0 : !labelValue);

  const isValidLabelValue = Array.isArray(labelValue)
    ? labelValue.length > 0
    : !!labelValue;
  const showIcon =
    requiredFieldCheck ||
    ((!!reviewProps || singlePageLayoutIcon) && isValidLabelValue);

  const isError =
    requiredFieldCheck ||
    (isValidLabelValue && !!errors[fieldProps.key]) ||
    defaultValues.isError;

  const questionsPanelClass =
    reviewProps || isSinglePageLayout || domainName === "LicenseForm"
      ? "questionClass"
      : undefined;

  if (fieldProps.hidden === true) return null;

  let recordList = null;
  if (fieldProps.type === "recordList") {
    recordList = (
      <FormRecordList
        setValue={setValue}
        fieldProps={fieldProps}
        formProps={newFormProps}
        control={control}
        trigger={triggerValidation}
        getValues={getValues}
        readonly
      />
    );
    labelValue = `( ${labelValue?.length ?? 0} )`;
  }

  let linkUrl = null;
  if (type === "invoice" && displayAs === "link") {
    linkUrl = getInvoiceUrl(newFormProps);
  }

  if (fieldProps.type === "licenseInfo") {
    return (
      <FormLicenseInfo
        fieldProps={fieldProps}
        getValues={getValues}
        formProps={formData}
      />
    );
  }
  const proTipDescription =
    fieldProps.type === "textarea" && description ? description : null;

  return (
    <Box className={questionsPanelClass} sx={{ position: "relative" }}>
      <Label
        label={fieldProps.label}
        value={labelValue}
        hasMaxWidth={fieldProps.type === "encrypted"}
        showIcon={showIcon}
        isError={isError}
        isDg={!reviewProps && !isSinglePageLayout}
        singlePageLayoutIcon={!!singlePageLayoutIcon}
        isSinglePageLayout={isSinglePageLayout}
        isMasked={!isValueMasked}
        showMaskedIcon={fieldProps.type === "encrypted"}
        handleMaskedToggle={handleMaskedToggle}
        tabIndex={0}
        linkUrl={linkUrl}
        proTipDescription={proTipDescription}
      />
      {recordList}
      {fieldProps.properties?.carbonCopy && emailValidationMsg && (
        <Info
          variant="body2"
          condition={{
            isError: !formData[`${fieldProps.key}Valid`],
            isDg: !reviewProps && !isSinglePageLayout,
          }}
        >
          {emailValidationMsg}
        </Info>
      )}
    </Box>
  );
}
FormReadOnlyField.propTypes = {
  fieldProps: PropTypes.shape({
    conditional: PropTypes.shape({
      when: PropTypes.string.isRequired,
      eq: PropTypes.string.isRequired,
      show: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]).isRequired,
    }),
    type: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    validate: PropTypes.shape({
      required: PropTypes.bool,
    }),
    properties: PropTypes.shape({
      skipOptionMapping: PropTypes.string,
    }),
    description: PropTypes.string,
  }).isRequired,
  hasConditionalRequired: PropTypes.bool,
  newFormProps: PropTypes.shape({}).isRequired,
  getValues: PropTypes.func.isRequired,
  register: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  trigger: PropTypes.func.isRequired,
  control: PropTypes.shape({}).isRequired,
};
FormReadOnlyField.defaultProps = {
  hasConditionalRequired: false,
};

export default FormReadOnlyField;
